import React, { useEffect, useRef, useState } from "react";
import { RecaptchaDisclaimer, useReCaptcha } from "@wss/re-captcha";
import {
  Button,
  Input,
  Form,
  ModalHeader,
  FormItem,
} from "@clarkinc/zest-design-system/ui";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  requestEmailLoginLink,
  getDefaultErrorStates,
  login,
} from "./LoginService";
import Errors from "./Errors";
import loginModalStore from "~/common/LoginModal/loginModalStore";
import { inputErrorMessage } from "../Form/inputStyles";

type LoginModalProps = {
  csrfToken: string;
};

const formSchema = z.object({
  email: z.string().optional(),
  password: z.string().optional(),
});

const LoginModal = ({ csrfToken }: LoginModalProps) => {
  const [errors, setErrors] = useState(getDefaultErrorStates());
  const [isAttemptingLogin, setIsAttemptingLogin] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isEmailLoginLinkSent, setIsEmailLoginLinkSent] = useState(false);
  const [isRequestingEmailLoginLink, setIsRequestingEmailLoginLink] =
    useState(false);
  const [emailFieldError, setEmailFieldError] = useState(false);
  const [passwordFieldError, setPasswordFieldError] = useState(false);

  const loginButtonDisabled =
    isAttemptingLogin || isRedirecting || isRequestingEmailLoginLink;
  const emailLinkButtonDisabled =
    isRedirecting || isAttemptingLogin || isRequestingEmailLoginLink;

  const reCaptcha = useReCaptcha();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const emailInputRef = useRef<HTMLInputElement>(null);
  const currentEmail = form.watch("email", "");

  const handleSubmit = async (
    data: z.infer<typeof formSchema>,
  ): Promise<void> => {
    setIsAttemptingLogin(true);
    setErrors(getDefaultErrorStates());
    setEmailFieldError(false);
    setPasswordFieldError(false);
    setIsRedirecting(false);

    const email = data.email ?? "";
    const password = data.password ?? "";

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      setEmailFieldError(!trimmedEmail);
      setPasswordFieldError(!trimmedPassword);
      setIsAttemptingLogin(false);
      return;
    }

    await login({
      email,
      password,
      csrfToken,
      setIsAttemptingLogin,
      setIsRedirecting,
      setErrors,
      reCaptcha,
    });
  };

  const onRequestEmailLoginLinkButtonClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();

    setErrors(getDefaultErrorStates());
    setIsRequestingEmailLoginLink(true);
    setIsEmailLoginLinkSent(false);
    setEmailFieldError(false);
    setPasswordFieldError(false);

    const email = currentEmail ?? "";

    if (!email.trim()) {
      setIsRequestingEmailLoginLink(false);
      setEmailFieldError(true);
      return;
    }

    await requestEmailLoginLink({
      email,
      csrfToken,
      errors,
      setErrors,
      setIsRequestingEmailLoginLink,
      setIsEmailLoginLinkSent,
      reCaptcha,
    });
  };

  const forgotPasswordLink = () => {
    return (
      <a
        href="/resetpassword-myaccount.html"
        className="text-xs leading-none mr-2"
        data-testid="forgot-password-link"
      >
        Forgot password?
      </a>
    );
  };

  useEffect(() => {
    if (
      errors.hasLoginError ||
      errors.hasServerError ||
      errors.hasCaptchaError
    ) {
      emailInputRef.current?.focus();
    }
  }, [errors]);

  const { targetUrl, queryParams } = loginModalStore.getState().options || {};

  return (
    <>
      <ModalHeader className="border-none p-0 m-0 font-semibold">
        Login
      </ModalHeader>
      <Form
        form={form}
        aria-label="login"
        onSubmit={form.handleSubmit(handleSubmit)}
        className="m-0 relative"
        data-testid="test-id"
      >
        {/* Inputs */}
        <div className="mt-3 lt:mt-5">
          <FormItem name="email">
            <Input
              helperText="Email Address"
              label="Email Address"
              hideLabel
              inputSize="large"
              type="email"
              name="email"
              maxLength={100}
              id="email"
              className="text-base sm:text-sm"
              data-testid="email-input"
              hasError={
                errors.hasLoginError ||
                errors.hasLoginEmailLinkError ||
                emailFieldError
              }
              errorMessages={emailFieldError ? "Email cannot be empty." : ""}
              ref={emailInputRef}
            />
          </FormItem>
          <div aria-live="polite">
            {errors.hasLoginEmailLinkError && (
              <p className={`mt-3 relative ${inputErrorMessage}`}>
                You entered incorrect login information, try again or{" "}
                <a href="/resetpassword-myaccount.html">reset your password</a>.
              </p>
            )}
          </div>
        </div>
        <div className="mt-3 relative lt:mt-5">
          <FormItem name="password">
            <Input
              helperText="Password"
              label="Password"
              hideLabel
              inputSize="large"
              suffix={forgotPasswordLink()}
              type="password"
              name="password"
              maxLength={100}
              id="password"
              className="text-base sm:text-sm"
              data-testid="password-input"
              hasError={errors.hasLoginError || passwordFieldError}
              errorMessages={
                passwordFieldError ? "Password cannot be empty." : ""
              }
            />
          </FormItem>
        </div>
        {/* Errors */}
        <Errors errors={errors} />
        {/* Login & Email Me Button */}
        <div className="mt-3 relative lt:mt-5">
          <Button
            theme="wss"
            type="submit"
            color="green"
            size="xlarge"
            full
            disabled={loginButtonDisabled}
            className="py-8 text-xl"
            data-testid="login-button"
          >
            Login
          </Button>
          <div className="text-2xs leading-3 my-4 text-center text-gray-600 mb-4 w-full">
            <RecaptchaDisclaimer />
          </div>
          <Button
            theme="wss"
            variant="secondary"
            color="gray"
            size="xlarge"
            full
            disabled={emailLinkButtonDisabled}
            type="button"
            name="emailLinkToLogin"
            className="text-base"
            onClick={onRequestEmailLoginLinkButtonClick}
            data-testid="email-to-login-button"
          >
            Email Me a Link to Login
          </Button>
        </div>
        <div aria-live="polite">
          {isEmailLoginLinkSent && (
            <div className="text-xs leading-3 mt-3 text-gray-600">
              We&#39;ve sent a login link to <strong>{currentEmail}</strong>
              <br />
              If you don&#39;t receive an email momentarily, please check your
              spam folder.
            </div>
          )}
        </div>
        {/* create an account link */}
        <span className="block text-sm leading-none mt-4 text-center">
          <a
            href={
              targetUrl
                ? `/myaccount.cfm?goto=register&target_url=${
                    targetUrl + (queryParams || "")
                  }`
                : "/myaccount.cfm?goto=register"
            }
          >
            Create an Account
          </a>
        </span>
      </Form>
    </>
  );
};

export default LoginModal;
